import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {Users, Fuel, Settings, ChevronRight, Sparkles, Shield, Zap, Calendar, Clock} from 'lucide-react'
import { useVehicles } from '@/hooks/useVehicles'
import { lumi } from '@/lib/lumi'
import ImageGallery from './ImageGallery'
import { computeVehicleAvailability } from '@/lib/availability'

function formatDateFr(dateStr: string): string {
  const date = new Date(dateStr)
  const day = date.getDate()
  const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']
  const month = months[date.getMonth()]
  const year = date.getFullYear()
  return `${day} ${month} ${year}`
}

interface VehicleAvailability {
  availableNow: boolean
  nextAvailableDate?: string
}

const categories = ['Tous', 'Citadine', 'Berline', 'SUV', 'Premium', 'Monospace', 'Fourgon', 'Plateau']

export default function FleetPage() {
  const [searchParams] = useSearchParams()
  const categorieParam = searchParams.get('categorie')
  const [selectedCategory, setSelectedCategory] = useState('Tous')
  const { vehicles, loading, fetchVehicles } = useVehicles()
  const [availabilityMap, setAvailabilityMap] = useState<Map<string, VehicleAvailability>>(new Map())
  const [reservationsLoading, setReservationsLoading] = useState(true)

  useEffect(() => {
    if (categorieParam && categories.includes(categorieParam)) {
      setSelectedCategory(categorieParam)
      window.scrollTo({ top: 0, behavior: 'instant' })
    }
  }, [categorieParam])

  useEffect(() => {
    fetchVehicles()
    // Charger les réservations actives/futures pour calculer la disponibilité
    const now = new Date().toISOString()
    lumi.entities.vehicle_bookings.list({
      filter: {
        status: { $in: ['pending', 'confirmed', 'paid', 'active'] },
        endDate: { $gte: now },
      },
      sort: { endDate: 1 },
      limit: 200,
    }).then((reservationsRes) => {
      const activeReservations = reservationsRes?.list || []
      const nowDate = new Date()

      // Regrouper les réservations par véhicule
      const vehicleReservationsMap = new Map<string, Array<{ startDate: string; endDate: string }>>()
      activeReservations.forEach((r: any) => {
        if (!vehicleReservationsMap.has(r.vehicleId)) {
          vehicleReservationsMap.set(r.vehicleId, [])
        }
        vehicleReservationsMap.get(r.vehicleId)!.push({
          startDate: r.startDate,
          endDate: r.endDate,
        })
      })

      const map = new Map<string, VehicleAvailability>()

      vehicleReservationsMap.forEach((reservations, vehicleId) => {
        const { availableNow, nextAvailableDate } = computeVehicleAvailability(reservations, nowDate)
        map.set(vehicleId, { availableNow, nextAvailableDate })
      })

      setAvailabilityMap(map)
    }).catch((err) => {
      console.error('Erreur chargement réservations:', err)
    }).finally(() => setReservationsLoading(false))
  }, [])

  // Exclure les véhicules cachés, mais inclure les indisponibles (visibles mais non réservables)
  const visibleStatuses = ['disponible', 'available', 'indisponible', 'unavailable', 'rented', 'maintenance']
  const isReservable = (status: string) => ['disponible', 'available'].includes(status)

  const filteredVehicles =
    selectedCategory === 'Tous'
      ? vehicles.filter((v) => visibleStatuses.includes(v.status))
      : vehicles.filter((v) => v.category === selectedCategory && visibleStatuses.includes(v.status))

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header avec effet parallaxe */}
      <section className="relative bg-black text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 via-transparent to-yellow-400/10" />
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-12 h-12 text-yellow-400 animate-pulse" />
            <h1 className="text-5xl md:text-6xl font-black bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 bg-clip-text text-transparent">
              Notre Flotte
            </h1>
          </div>
          <p className="text-xl text-gray-300 max-w-2xl">
            Des véhicules premium pour tous vos déplacements — qualité, confort et performance garantis
          </p>
        </div>
      </section>

      {/* Filters avec style moderne */}
      <section className="sticky top-16 z-40 bg-black/95 backdrop-blur-lg border-b border-yellow-400/20 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex gap-3 overflow-x-auto pb-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-xl font-bold whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black shadow-lg shadow-yellow-400/50 scale-105'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-16 h-16 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mb-4" />
              <div className="text-2xl text-white font-medium">Chargement des véhicules...</div>
            </div>
          ) : (
            <>
              <div className="mb-8 flex items-center gap-3">
                <div className="h-1 flex-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
                <p className="text-yellow-400 font-bold text-lg">
                  {filteredVehicles.length} véhicule{filteredVehicles.length > 1 ? 's' : ''} disponible{filteredVehicles.length > 1 ? 's' : ''}
                </p>
                <div className="h-1 flex-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
              </div>

              {filteredVehicles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-10 shadow-2xl border-2 border-yellow-400/30 max-w-lg w-full text-center">
                    <div className="w-20 h-20 bg-yellow-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Calendar className="w-10 h-10 text-yellow-400" />
                    </div>
                    <h3 className="text-2xl font-black text-white mb-3">
                      Aucun véhicule trouvé
                    </h3>
                    <p className="text-gray-400 mb-8">
                      {selectedCategory !== 'Tous'
                        ? `Aucun véhicule disponible dans la catégorie "${selectedCategory}" pour le moment.`
                        : 'Aucun véhicule ne correspond à votre recherche pour le moment.'}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      {selectedCategory !== 'Tous' && (
                        <button
                          onClick={() => setSelectedCategory('Tous')}
                          className="inline-flex items-center justify-center gap-2 bg-gray-700 text-white px-6 py-3 rounded-xl font-bold hover:bg-gray-600 transition-all"
                        >
                          Voir tous les véhicules
                        </button>
                      )}
                      <a
                        href="tel:+41218005050"
                        className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-6 py-3 rounded-xl font-bold hover:shadow-2xl hover:shadow-yellow-400/50 transition-all hover:scale-105"
                      >
                        <Shield className="w-5 h-5" />
                        Nous contacter
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredVehicles.map((vehicle) => {
                      const isReservableVehicle = isReservable(vehicle.status)
                      const availability = availabilityMap.get(vehicle._id)
                      // Si pas de réservation pour ce véhicule, on calcule quand même la disponibilité
                      // (prise en compte de l'heure du jour)
                      const { availableNow, nextAvailableDate } = availability
                        ?? computeVehicleAvailability([], new Date())

                      return (
                  <div
                    key={vehicle._id}
                    className={`group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden hover:shadow-2xl transition-shadow duration-300 border-2 ${!isReservableVehicle ? 'border-red-500/30 hover:border-red-500/60 hover:shadow-red-500/10 opacity-80' : 'border-gray-700 hover:border-yellow-400 hover:shadow-yellow-400/20'}`}
                  >
                    {/* Image avec overlay */}
                    <div className="relative h-56 overflow-hidden">
                      <ImageGallery
                        mainImage={vehicle.imageUrl}
                        images={vehicle.images}
                        alt={vehicle.name}
                        height="h-56"
                        showCounter={true}
                        vehicleId={vehicle._id}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
                      <div className="absolute top-4 right-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-4 py-2 rounded-full text-sm font-bold shadow-lg pointer-events-none">
                        {vehicle.category}
                      </div>
                      {/* Badge de disponibilité */}
                      <div className="absolute top-4 left-4 pointer-events-none">
                        {!isReservableVehicle ? (
                          <span className="inline-flex items-center gap-1.5 bg-red-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                            <Shield className="w-3 h-3" />
                            Indisponible
                          </span>
                        ) : availableNow ? (
                          <span className="inline-flex items-center gap-1.5 bg-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                            Disponible
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 bg-orange-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                            <Clock className="w-3 h-3" />
                            En location
                          </span>
                        )}
                      </div>
                      <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                        <h3 className="text-2xl font-black text-white mb-1 drop-shadow-lg">{vehicle.name}</h3>
                        <p className="text-yellow-400 text-sm font-medium">{vehicle.brand} {vehicle.model}</p>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      {/* Availability info */}
                      {!isReservable(vehicle.status) ? (
                        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2.5 rounded-xl mb-5">
                          <Shield className="w-4 h-4 text-red-400 flex-shrink-0" />
                          <p className="text-sm font-semibold">Ce véhicule est actuellement en location</p>
                        </div>
                      ) : !availableNow && nextAvailableDate ? (
                        <div className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 text-orange-400 px-4 py-2.5 rounded-xl mb-5">
                          <Calendar className="w-4 h-4 text-orange-400 flex-shrink-0" />
                          <p className="text-sm font-semibold">
                            Disponible dès le <span className="font-black text-orange-300">{formatDateFr(nextAvailableDate)}</span>
                          </p>
                        </div>
                      ) : isReservableVehicle && availableNow ? (
                        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-2.5 rounded-xl mb-5">
                          <span className="w-2.5 h-2.5 bg-emerald-400 rounded-full animate-pulse flex-shrink-0" />
                          <p className="text-sm font-semibold">Disponible immédiatement</p>
                        </div>
                      ) : null}

                      {/* Features Grid */}
                      <div className="grid grid-cols-3 gap-3 mb-6">
                        <div className="flex flex-col items-center gap-2 bg-gray-700/50 p-3 rounded-lg border border-gray-600 group-hover:border-yellow-400/50 transition-colors">
                          <Users className="w-5 h-5 text-yellow-400" />
                          <span className="text-white font-bold text-sm">{vehicle.seats}</span>
                          <span className="text-gray-400 text-xs">Places</span>
                        </div>
                        <div className="flex flex-col items-center gap-2 bg-gray-700/50 p-3 rounded-lg border border-gray-600 group-hover:border-yellow-400/50 transition-colors">
                          <Settings className="w-5 h-5 text-yellow-400" />
                          <span className="text-white font-bold text-xs text-center">{vehicle.transmission}</span>
                        </div>
                        <div className="flex flex-col items-center gap-2 bg-gray-700/50 p-3 rounded-lg border border-gray-600 group-hover:border-yellow-400/50 transition-colors">
                          <Fuel className="w-5 h-5 text-yellow-400" />
                          <span className="text-white font-bold text-xs text-center">{vehicle.fuel}</span>
                        </div>
                      </div>

                      {/* Features badges */}
                      {vehicle.features && vehicle.features.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-6">
                          {vehicle.features.slice(0, 3).map((feature: string, idx: number) => (
                            <span key={idx} className="flex items-center gap-1 px-3 py-1 bg-gray-700 text-gray-300 text-xs rounded-full border border-gray-600">
                              <Zap className="w-3 h-3 text-yellow-400" />
                              {feature}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Price and CTA */}
                      <div className="flex items-center justify-between pt-6 border-t border-gray-700">
                        <div>
                          {vehicle.pricePerHalfDay && vehicle.pricePerHalfDay > 0 ? (
                            <div className="flex items-end gap-3">
                              <div>
                                <div className="text-sm font-bold text-gray-400 mb-0.5">½ J</div>
                                <div className="text-xl font-black bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent leading-none">
                                  CHF {vehicle.pricePerHalfDay}
                                </div>
                              </div>
                              <div className="w-px h-10 bg-gray-600" />
                              <div>
                                <div className="text-sm font-bold text-gray-400 mb-0.5">J</div>
                                <div className="text-xl font-black bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent leading-none">
                                  CHF {vehicle.pricePerDay}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="text-3xl font-black bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent">
                                CHF {vehicle.pricePerDay}
                              </div>
                              <div className="text-sm text-gray-400 font-medium">par jour</div>
                            </>
                          )}
                          <div className="text-xs text-gray-500 mt-1">
                            {vehicle.includedKm && vehicle.includedKm > 0
                              ? <>{vehicle.includedKm} km inclus{vehicle.extraKmPrice > 0 && <span className="text-orange-400"> • +CHF {vehicle.extraKmPrice.toFixed(2)}/km</span>}</>
                              : <span className="text-emerald-400">Km illimités</span>
                            }
                          </div>
                        </div>
                        {isReservableVehicle ? (
                          <Link
                            to={`/reserver?vehicleId=${vehicle._id}`}
                            className={`flex items-center gap-2 px-6 py-3 rounded-xl hover:shadow-xl transition-all group font-bold hover:scale-105 ${
                              availableNow
                                ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black hover:shadow-yellow-400/50'
                                : 'bg-orange-500 text-white hover:shadow-orange-500/50'
                            }`}
                          >
                            {availableNow ? 'Réserver' : 'Pré-réserver'}
                            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        ) : (
                          <span className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold bg-gray-600 text-gray-400 cursor-not-allowed">
                            Indisponible
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                      )
                    })}
              </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section avec dégradé */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/10 via-yellow-500/5 to-yellow-400/10" />
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-12 shadow-2xl border-2 border-yellow-400/30">
            <Sparkles className="w-16 h-16 text-yellow-400 mx-auto mb-6 animate-pulse" />
            <h2 className="text-4xl font-black text-white mb-4">
              Vous ne trouvez pas ce que vous cherchez ?
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Contactez-nous et nous trouverons le véhicule parfait pour vos besoins
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-yellow-400/50 transition-all hover:scale-105"
            >
              <Shield className="w-6 h-6" />
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
